import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import activityService, { 
  Activity, 
  ActivityAnalysisRequest, 
  ActivityCreateRequest, 
  ActivityUpdateRequest,
  ActivityReorderRequest 
} from '@/services/activityService';
import ToastService from '@/utils/toast';

export const useActivityData = () => {
  const queryClient = useQueryClient();

  // Fetch user activities
  const {
    data: activitiesData,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['activities'],
    queryFn: () => activityService.getUserActivities(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch missing activity info
  const {
    data: missingInfo,
    isLoading: missingInfoLoading
  } = useQuery({
    queryKey: ['activities', 'missing-info'],
    queryFn: () => activityService.getMissingActivityInfo(),
    staleTime: 5 * 60 * 1000,
  });

  // Analyze activity mutation
  const analyzeActivityMutation = useMutation({
    mutationFn: (request: ActivityAnalysisRequest) => 
      activityService.analyzeActivity(request),
    onError: (error) => {
      console.error('Activity analysis failed:', error);
      ToastService.showError('Failed to analyze activity. Please try again.');
    }
  });

  // Create activity mutation
  const createActivityMutation = useMutation({
    mutationFn: (activityData: ActivityCreateRequest) => 
      activityService.createActivity(activityData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      ToastService.showSuccess('Activity added successfully!');
    },
    onError: (error) => {
      console.error('Failed to create activity:', error);
      ToastService.showError('Failed to add activity. Please try again.');
    }
  });

  // Update activity mutation
  const updateActivityMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: ActivityUpdateRequest }) => 
      activityService.updateActivity(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      ToastService.showSuccess('Activity updated successfully!');
    },
    onError: (error) => {
      console.error('Failed to update activity:', error);
      ToastService.showError('Failed to update activity. Please try again.');
    }
  });

  // Delete activity mutation
  const deleteActivityMutation = useMutation({
    mutationFn: (activityId: number) => 
      activityService.deleteActivity(activityId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      ToastService.showSuccess('Activity deleted successfully!');
    },
    onError: (error) => {
      console.error('Failed to delete activity:', error);
      ToastService.showError('Failed to delete activity. Please try again.');
    }
  });

  // Reorder activities mutation
  const reorderActivitiesMutation = useMutation({
    mutationFn: (activities: Activity[]) => {
      const reorderRequest: ActivityReorderRequest = {
        activity_orders: activities.map((activity, index) => ({
          id: activity.id,
          order_index: index
        }))
      };
      return activityService.reorderActivities(reorderRequest);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      ToastService.showSuccess('Activities reordered successfully!');
    },
    onError: (error) => {
      console.error('Failed to reorder activities:', error);
      ToastService.showError('Failed to reorder activities. Please try again.');
    }
  });

  return {
    // Data
    activities: activitiesData?.activities || [],
    totalCount: activitiesData?.total_count || 0,
    missingInfo: missingInfo || [],
    
    // Loading states
    isLoading,
    missingInfoLoading,
    isAnalyzing: analyzeActivityMutation.isPending,
    isCreating: createActivityMutation.isPending,
    isUpdating: updateActivityMutation.isPending,
    isDeleting: deleteActivityMutation.isPending,
    isReordering: reorderActivitiesMutation.isPending,
    
    // Error states
    error,
    
    // Actions
    analyzeActivity: analyzeActivityMutation.mutateAsync,
    createActivity: createActivityMutation.mutateAsync,
    updateActivity: updateActivityMutation.mutateAsync,
    deleteActivity: deleteActivityMutation.mutateAsync,
    reorderActivities: reorderActivitiesMutation.mutateAsync,
    refetch,
    
    // Analysis result
    analysisResult: analyzeActivityMutation.data,
    analysisError: analyzeActivityMutation.error
  };
};